import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'i18n'
import { FormBuilder } from 'components/FormBuilder'
import { useForm } from 'react-hook-form'
import { generateDynamicYupSchema } from 'components/FormBuilder/generateYupSchemaFromJson'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { UserVerification } from 'components/UserVerification/UserVerification'
import { Dialog, IconButton, CircularProgress } from '@mui/material'
import { Turnstile } from '@marsidev/react-turnstile'
import { useTurnstile } from 'hooks/useTurnstile'
import { LegalTerm } from 'components/FormBuilder/Fields/LegalTerm'
import { createCheckboxValidationSchema } from 'components/forms/validations'
import closeLogo from 'images/icon-close.svg'
import incomOfferFormFieldsJson from './incomOfferFormFields.json'
import * as styles from './IncommOfferForm.module.scss'
import {
  IncommBarcodeFormProps,
  normalizeCheckboxesForForm,
} from '../../utils'
import {
  getBannerStoresByIds,
  getBannerStoreById,
  getStoreGroups,
} from '../../constants/banner-stores'
import {
  generateIncommTokenService,
  validateTokenAndGenerateBarcode,
} from 'services/incomm/token'
import * as Yup from 'yup'
import { IncommOfferPicker, OfferOption } from './IncommOfferPicker'

export interface IncommMultipleOfferStepProps {
  offers: OfferOption[]
  incommBarcodeForm?: IncommBarcodeFormProps
}

export const IncommOfferMultipleForm = ({
  offers,
  incommBarcodeForm,
}: IncommMultipleOfferStepProps) => {
  const { t } = useTranslation(['incommOffer', 'common'])
  const {
    ctaLabel = 'Get Offer',
    selectedStoreId = '',
    onSubmit: onSubmitProp,
    formError,
    skipVerification,
    testToken,
    autoSendEmail,
    checkboxes = [],
    onStatusModalOpen,
  } = incommBarcodeForm || {}

  if (!incommBarcodeForm) {
    throw new Error('Barcode form is required')
  }

  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null)
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)
  const [isInProgress, setIsInProgress] = useState(false)
  const [storeGroups, setStoreGroups] = useState<any[]>([])
  const { turnstileRef, turnstileToken, setTurnstileToken } = useTurnstile()
  const [turnstileReady, setTurnstileReady] = useState(false)
  const checkboxFields = useMemo(
    () => normalizeCheckboxesForForm(checkboxes),
    [checkboxes],
  )

  // Handle offer selection and check for expired status
  const handleOfferSelection = (offerId: string) => {
    const selectedOffer = offers.find(o => String(o.offerId) === String(offerId))
    if (selectedOffer?.status?.toUpperCase() === 'EXPIRED') {
      onStatusModalOpen?.('expired')
      return
    }
    setSelectedOfferId(offerId)
  }

  // Get available store IDs for the selected offer
  const availableStoreIds = useMemo(() => {
    if (!selectedOfferId) return []
    const selectedOffer = offers.find(o => String(o.offerId) === String(selectedOfferId))
    return selectedOffer?.availableStoreIds || []
  }, [selectedOfferId, offers])

  // Load store groups data asynchronously and filter by available stores
  useEffect(() => {
    const loadStoreGroups = async () => {
      const allGroups = await getStoreGroups()

      if (availableStoreIds.length > 0) {
        // Filter groups to only include stores that are in availableStoreIds
        const filteredGroups = allGroups
          .map(group => ({
            ...group,
            bannerStores: group.bannerStores.filter(store =>
              availableStoreIds.includes(store.bannerStoreId)
            )
          }))
          .filter(group => group.bannerStores.length > 0)

        setStoreGroups(filteredGroups)
      } else {
        // If no specific stores are available, show all stores
        setStoreGroups(allGroups)
      }
    }
    loadStoreGroups()
  }, [availableStoreIds])

  // Dynamically generate dropdown options from store groups
  const dynamicFormFields = useMemo(() => {
    const dropdownOptions: any[] = []

    storeGroups.forEach(group => {
      // Add stores in this group with group identifier
      group.bannerStores.forEach(store => {
        dropdownOptions.push({
          value: store.bannerStoreId,
          label: store.name,
          group: group.storeGroupName,
        })
      })
    })

    // Clone the fields and update the selectedBannerStoreId field options
    const fields = incomOfferFormFieldsJson.fields.map(field => {
      if (field.name === 'selectedBannerStoreId') {
        return {
          ...field,
          options: dropdownOptions,
        }
      }
      return field
    })

    return fields
  }, [storeGroups])

  const jsonValidationYup = useMemo(() => {
    const baseSchema = generateDynamicYupSchema(dynamicFormFields)
    const schemaObject: any = { ...baseSchema.fields }
    const checkboxValidations = createCheckboxValidationSchema(checkboxFields)

    return Yup.object().shape({ ...schemaObject, ...checkboxValidations })
  }, [dynamicFormFields, checkboxFields])

  // Build default values including checkboxes
  const defaultValues = useMemo(() => {
    const defaults: any = {
      email: '',
      selectedStoreId: selectedStoreId,
      availableStoreIds: availableStoreIds,
    }

    // Add checkbox default values
    checkboxFields.forEach(checkbox => {
      defaults[checkbox.fieldName] = false
    })

    return defaults
  }, [selectedStoreId, availableStoreIds, checkboxFields])

  const form = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: useYupValidationResolver(jsonValidationYup),
  })

  const {
    handleSubmit,
    register,
    getValues,
    control,
    formState: { errors },
  } = form

  const onEmailVerifiedConfirm = async ({ values }) => {
    try {
      // Barcode generation already happened during validation!
      // The custom validation service returned the barcode data
      const valuesFromForm = getValues()

      // Collect checkbox values
      const checkboxValues: Record<string, boolean> = {}
      checkboxFields.forEach(checkbox => {
        checkboxValues[checkbox.id] = valuesFromForm[checkbox.fieldName] || false
      })

      // Pass the barcode response to parent via onSubmit callback
      await onSubmitProp({
        ...values,
        selectedStoreId: valuesFromForm.selectedBannerStoreId || null,
        selectedOfferId,
        checkboxes: checkboxValues,
      })

      setIsInProgress(false)
      handleClose()
    } catch (error) {
      console.error('Error processing barcode:', error)
      setIsInProgress(false)
    }
  }

  const getCheckboxesAccepted = () => {
    const valuesFromForm = getValues()
    return checkboxFields.map(checkbox => ({
      checkboxId: checkbox.id,
      label: checkbox.label,
      required: Boolean(checkbox.required),
      accepted: Boolean(valuesFromForm[checkbox.fieldName]),
    }))
  }

  const onValidationError = (error: any) => {
    console.log('onValidationError called with:', error)
    
    // Close the verification dialog
    handleClose()
    
    // Check if error message contains clipLimitExceeded
    // doPost2ErrorHandling throws: { status, success: false, detail: "error message" }
    const errorMessage = error?.detail || error?.message || String(error)
    console.log('Parsed error message:', errorMessage)
    
    const errorString = String(errorMessage).toLowerCase()
    if (errorString.includes('cliplimitexceeded') || 
        errorString.includes('clip limit exceeded')) {
      console.log('Opening clip_limit_exceeded modal')
      onStatusModalOpen?.('clip_limit_exceeded')
    } else {
      console.log('Error does not match clipLimitExceeded pattern')
    }
  }

  const onSubmit = async data => {
    if (!selectedOfferId) {
      return // Button should be disabled, but extra safety check
    }

    // Get barcodeFormat from the selected banner store
    const selectedStore = await getBannerStoreById(data.selectedBannerStoreId)
    const barcodeFormat = selectedStore?.barcodeFormat || 'CODE_128'

    setIsInProgress(true)
    if (skipVerification) {
      // Skip verification for testing - call barcode API directly
      try {
        const response = await validateTokenAndGenerateBarcode({
          email: data.email,
          token: testToken || 'SKIP_VERIFICATION',
          offerId: String(selectedOfferId),
          selectedBannerStoreId: data.selectedBannerStoreId,
          barcodeFormat,
          autoSendEmail,
          checkboxesAccepted: getCheckboxesAccepted(),
        })

        onEmailVerifiedConfirm({ values: response })
      } catch (error: any) {
        console.error('Error generating barcode:', error)
        
        // Check if error message contains clipLimitExceeded
        const errorMessage = error?.message || error?.detail || String(error)
        if (errorMessage.toLowerCase().includes('cliplimitexceeded') || 
            errorMessage.toLowerCase().includes('clip limit exceeded')) {
          onStatusModalOpen?.('clip_limit_exceeded')
        }
        
        setIsInProgress(false)
      }
      return
    }

    // Ensure we have a valid turnstile token before opening modal
    if (!turnstileToken) {
      console.error('Turnstile token not available')
      setIsInProgress(false)
      return
    }

    setVerificationDialogOpen(true)
  }

  const handleClose = () => {
    setVerificationDialogOpen(false)
    setIsInProgress(false)
  }

  return (
    <div className={styles.formContainer}>
      <IncommOfferPicker
        offers={offers}
        selectedOfferId={selectedOfferId}
        onSelectOffer={handleOfferSelection}
      />

      <FormBuilder
        className={styles.incommForm}
        jsonFieldOnForm={true}
        fieldStyle={{}}
        fields={dynamicFormFields}
        formHeader={null}
        form={form}
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        setFieldValue={form.setValue}
        disableValidation={true}
        disabledFields={[]}
      >
        {/* Render checkboxes if provided */}
        {checkboxFields.length > 0 && (
          <div className={styles.checkboxesContainer}>
            {checkboxFields.map(checkbox => (
              <LegalTerm
                key={checkbox.fieldName}
                name={checkbox.fieldName}
                label={checkbox.label}
                register={register}
                error={errors[checkbox.fieldName]}
                required={checkbox.required}
                control={control}
                defaultChecked={false}
                disabled={false}
              />
            ))}
          </div>
        )}
        {/* Turnstile component visible in form - token captured before modal opens */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          <Turnstile
            ref={turnstileRef}
            options={{
              refreshExpired: 'manual',
            }}
            siteKey={process.env.VISIBLE_TURNSTILE_SITE_KEY}
            onSuccess={token => {
              setTurnstileToken(token)
              setTurnstileReady(true)
            }}
            onError={error => {
              console.error('Turnstile error:', error)
              setTurnstileToken(null)
              setTurnstileReady(false)
            }}
            onExpire={() => {
              console.log('Turnstile token expired')
              setTurnstileToken(null)
              setTurnstileReady(false)
              turnstileRef.current?.reset()
            }}
          />
        </div>
        <button
          className={styles.submitButton}
          disabled={isInProgress || !selectedOfferId || !turnstileReady}
          type="submit"
        >
          {isInProgress ? (
            <CircularProgress color="inherit" size={18} />
          ) : (
            ctaLabel
          )}
        </button>
      </FormBuilder>
      {formError && <div className={styles.error}>{formError}</div>}
      <Dialog
        onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}
        open={verificationDialogOpen}
      >
        <IconButton
          aria-label={t('common:close')}
          onClick={handleClose}
          sx={{
            right: 8,
            top: 8,
            alignSelf: 'end',
          }}
        >
          <img src={closeLogo} alt={t('common:close')} />
        </IconButton>
        <UserVerification
          hasTurnstile={false}
          onConfirm={onEmailVerifiedConfirm}
          onValidationError={onValidationError}
          onTokenAlreadyRequested={() =>
            onStatusModalOpen?.('already_requested')
          }
          customGenerateTokenService={generateIncommTokenService}
          customValidateTokenService={validateTokenAndGenerateBarcode}
          tokenServicePayload={{
            offerId: selectedOfferId ? String(selectedOfferId) : '',
            selectedBannerStoreId: getValues().selectedBannerStoreId,
            barcodeFormat:
              storeGroups
                .flatMap(group => group.bannerStores)
                .find(store => store.bannerStoreId === getValues().selectedBannerStoreId)
                ?.barcodeFormat || 'CODE_128',
            turnstileToken, // Pass the captured turnstile token from form
            autoSendEmail,
            checkboxesAccepted: getCheckboxesAccepted(),
          }}
          {...getValues()}
        />
      </Dialog>
    </div>
  )
}
